import { action, observable, makeObservable, computed } from 'mobx';
import { BaseViewStore, HyperlinkCellData } from 'core/stores';
import FulfillmentOrderProcessingStore from './FulfillmentOrderProcessingStore';
import FulfillmentOrderStore from './FulfillmentOrderStore';
import ShipmentDocumentStore from './ShipmentDocumentStore';
import { PackingSlipStore } from 'modules/packing-slip/stores';
import { LookupStore } from 'modules/lookup/stores';
import { ProductStore } from 'modules/product/stores';
import { LabelingStepForm, DimensionsStepForm, FinalStepForm } from 'modules/order/forms/labeling-mode';
import { PalletCreateForm } from 'modules/order/forms';
import {
	isSeller,
	isSuperAdminOrganization,
	isWarehouse,
	createFileUrl,
	notificationError,
	translate,
	isSmartshipWarehouseAdministrator,
	isSome,
} from 'core/utils';
import {
	lookupRouteName,
	sourceOfChange,
	fulfillmentOrderStatus,
	shipmentInfoStatusEnum,
	shipmentDocumentSourceEnum,
	shipmentDocumentTypeEnum,
} from 'common/constants';
import { BaasicDropdownViewStore } from 'common/stores';
import { ApplicationStore } from 'platform/application/stores';
import { ShipmentInfoStore } from 'modules/shipment-info/stores';
import { SoundService } from 'modules/sound/services';
import PalletStore from './PalletStore';
import moment from 'moment';
import printJS from 'print-js';
import _ from 'lodash';

const printDocumentTimeout = 30000;

class labelingModeViewStore extends BaseViewStore {
	@observable scannedUpc = '';
	@observable barcode = null;
	@observable isUpcScanned = false;
	@observable isFulfillmentOrderIdEntered = false;
	@observable fulfillmentOrders = [];
	@observable activeStep = {};
	@observable scanUpcMessage = null;
	@observable scanUpcMessageData = null;
	@observable scanLabelMessage = null;
	@observable closeBoxMessage = null;
	@observable createLabelMessage = null;
	@observable dimensionsStepErrorMessage = null;
	@observable palletScanMessage = null;
	@observable infiniteSearchPhrase = null;
	@observable showCreateTemplate = false;
	@observable newFulfillmentOrderCreated = false;
	@observable selectedFulfillmentOrder = null;
	@observable initialFulfillmentOrder = null;
	@observable hasPackingSlip = false;
	@observable hasUpcLabel = false;
	@observable hasGs1Label = false;
	@observable closeBoxDisabled = false;
	@observable lastAction = null;
	@observable timeoutAction = null;
	@observable actionsDone = [];
	@observable undoDisabled = false;
	@observable isFOSplitted = false;
	@observable createPalletMode = false;
	@observable createPalletNotification = null;
	@observable steps = {};
	@observable palletDropdownStore = null;
	@observable warehouseDropdownStore = null;
	@observable data = [];
	selectedItem;
	tableData = null;
	originalBarcode = null;
	barcodeRef = null;
	selectReference = null;
	product = null;
	activeShipmentInfo = null;
	nonScannedItems = [];
	packagedFulfillmentOrders = [];
	boxedFulfillmentOrders = [];
	splittedFulfillmentOrderIds = [];
	label;
	shouldPrintPackingSlip = false;

	getRemoveFromPalletConfirmModalProps = item => {
		return {
			onConfirm: async () => await this.removeFromPallet(this.rootStore.viewStore.selectedConfirmModal.data),
			message: ['EXITSCANDETAILVIEW.MODAL.REMOVEFROMPALLET', { name: item.fulfillmentOrder.fulfillmentOrderId }],
			data: item,
		};
	};

	palletCreateForm = new PalletCreateForm({
		onSuccess: async () => {
			await this.createPallet();
		},
	});

	constructor(rootStore, { modalParams }) {
		super(rootStore);
		makeObservable(this);
		this.modalParams = modalParams;
		this.isLtlPackingMode = modalParams.data.isLtlPackingMode;
		this.useRegularPalletizingMode = rootStore.orderListViewStore.useRegularPalletizingMode;
		this.fulfillmentOrderProcessingStore = new FulfillmentOrderProcessingStore(rootStore);
		this.fulfillmentOrderStore = new FulfillmentOrderStore(rootStore);
		this.packingSlipStore = new PackingSlipStore(rootStore);
		this.shipmentDocumentStore = new ShipmentDocumentStore(rootStore);
		this.shipmentInfoStore = new ShipmentInfoStore(rootStore);
		this.productStore = new ProductStore(rootStore);
		this.lookupStore = new LookupStore(rootStore);
		this.soundService = new SoundService();
		this.isSeller = isSeller(rootStore);
		this.isWarehouse = isWarehouse(rootStore);
		this.isSuperAdminOrganization = isSuperAdminOrganization(rootStore);
		this.isSmartshipWarehouseAdministrator = isSmartshipWarehouseAdministrator(rootStore);
		this.palletStore = new PalletStore(this.rootStore);
		this.initialize();
	}

	@action.bound
	initialize = async () => {
		this.suspend();
		if (this.isLtlPackingMode) {
			this.fulfillmentOrderStatuses = (await this.lookupStore.find(
				{ rpp: 50, page: 1 },
				lookupRouteName.fulfillmentOrderStatus
			)).data.item;
		}
		if (!this.useRegularPalletizingMode) {
			await this.initializePalletDropdown();
			this.carrierTypeServiceTypeDropdownStore = new BaasicDropdownViewStore();
			await this.carrierTypeServiceTypeDropdownStore.initialize({
				field: this.palletCreateForm.$('carrierTypeServiceTypeId'),
				lookupRoute: lookupRouteName.carrierTypeServiceType,
				store: new LookupStore(this.rootStore),
				onChangeCallback: option => {
					if (option) this.barcode = `${option.extra.code}-${this.originalBarcode}`;
				},
			});
			if (this.isSmartshipWarehouseAdministrator) {
				this.warehouseDropdownStore = new BaasicDropdownViewStore();
				await this.warehouseDropdownStore.initialize({
					store: new ApplicationStore(this.rootStore),
					filterProperties: { warehouseOnly: true, isOrganizationActive: true },
					itemTemplate: {
						value: 'id',
						label: 'applicationTitle',
					},
					isRequired: true,
					onChangeCallback: async wh => this.onWarehouseChange(wh),
				});
			}
			this.defineFOTableData();
		}
		this.initializeSteps();
		this.boxDropdownStore = new BaasicDropdownViewStore();
		await this.boxDropdownStore.initialize({
			field: this.steps.dimensionsStep.form.$('boxId'),
			store: new LookupStore(this.rootStore),
			lookupRoute: lookupRouteName.box,
			onChangeCallback: value => this.onBoxChange(value),
			fetchInitialValues: false,
			headerOption: { value: null, label: 'Add new template' },
		});
		const dimensionsForm = this.steps.dimensionsStep.form;
		if (this.isLtlPackingMode) {
			// LTL captures per-product pallet dimensions instead of a box template; the box dropdown isn't shown, so
			// relax boxId and let the print-BOL gate validate the pallet rows.
			dimensionsForm.$('boxId').set('rules', '');
			dimensionsForm.$('boxId').resetValidation();
		}
		// Non-LTL leaves the `pallets` array empty, so its per-row rules never fire — no relaxation needed.
		this.resume();
	};

	@action.bound
	initializePalletDropdown = async () => {
		this.palletDropdownStore = new BaasicDropdownViewStore();
		await this.palletDropdownStore.initialize({
			store: new PalletStore(this.rootStore),
			itemTemplate: {
				value: 'id',
				label: 'barcode',
			},
			isRequired: true,
			filterProperties: {
				embed: [
					'shipmentInfos',
					'shipmentInfos.fulfillmentOrder',
					'shipmentInfos.fulfillmentOrder.order',
					'shipmentInfos.fulfillmentOrder.order.channel',
					'shipmentInfos.fulfillmentOrder.order.channel.channelType',
					'shipmentInfos.fulfillmentOrder.onHoldReason',
					'shipmentInfos.fulfillmentOrder.fulfillmentOrderStatus',
					'shipmentInfos.fulfillmentOrder.fulfillmentOrderItems',
					'shipmentInfos.fulfillmentOrder.fulfillmentOrderItems.product',
					'carrierTypeServiceType',
				],
				isOpened: true,
				isSeller: isSeller(this.rootStore),
				warehouseIds:
					this.isSmartshipWarehouseAdministrator && _.get(this.warehouseDropdownStore, 'value')
						? [this.warehouseDropdownStore.value.value]
						: null,
			},
			onChangeCallback: this.populateTableCallback,
		});
	};

	@action.bound
	initializeSteps = () => {
		this.steps = {
			selectingFoStep: {
				title: 'FULFILLMENTORDER.LABELINGMODE.STEPS.SELECTINGFO',
				number: 1,
				isActive: true,
				isCompleted: false,
			},
			labelingStep: {
				title: 'FULFILLMENTORDER.LABELINGMODE.STEPS.LABELING',
				number: 2,
				isActive: false,
				isCompleted: false,
				form: new LabelingStepForm(),
			},
			dimensionsStep: {
				title: 'FULFILLMENTORDER.LABELINGMODE.STEPS.DIMENSIONS',
				number: 3,
				isActive: false,
				isCompleted: false,
				form: new DimensionsStepForm({
					onSuccess: () => {
						this.onCreateTemplateSubmit();
					},
				}),
			},
			finalStep: {
				title: this.useRegularPalletizingMode
					? 'FULFILLMENTORDER.LABELINGMODE.STEPS.PACKAGING'
					: 'FULFILLMENTORDER.LABELINGMODE.STEPS.PALLETIZING',
				number: 4,
				isActive: false,
				isCompleted: false,
				form: new FinalStepForm(),
			},
		};
	};

	@action.bound
	onSearchChange = async () => {};

	@action.bound
	deletePallet = async () => {
		this.suspend();
		try {
			const option = this.rootStore.viewStore.selectedConfirmModal.data;
			await this.palletStore.delete({ id: option.value });
			this.rootStore.notificationStore.success(['LOOKUPDELETE.PALLET', { name: option.label }]);
			this.palletDropdownStore.clear();
			await this.palletDropdownStore.reload();
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	};

	@action.bound
	displayDeleteAction = data => {
		if (
			data.extra != null &&
			data.extra.shipmentInfos != null &&
			data.extra.shipmentInfos.length == 0 &&
			!this.isSeller
		) {
			return true;
		} else {
			return false;
		}
	};

	@action.bound
	populateTableCallback = selectedValue => {
		if (!selectedValue) return;

		this.data = [];
		_.each(selectedValue.extra.shipmentInfos, shipmentInfo => {
			if (shipmentInfo != null && shipmentInfo.fulfillmentOrder != null) {
				let product = {};
				if (shipmentInfo.fulfillmentOrder.fulfillmentOrderItems.length != 1) {
					product.name = 'Multiple';
					product.sku = 'Multiple';
				} else {
					product.name = shipmentInfo.fulfillmentOrder.fulfillmentOrderItems[0].product.name;
					product.sku = shipmentInfo.fulfillmentOrder.fulfillmentOrderItems[0].product.mainSku;
				}
				shipmentInfo.fulfillmentOrder.product = product;
				this.data.push(shipmentInfo);
			}
		});
	};

	@action.bound
	removeFromPallet = async item => {
		this.suspend();
		try {
			await this.shipmentInfoStore.removeFromPallet({
				id: item.id,
				sourceOfChange: sourceOfChange.palletizingModeRemoveFromPallet,
			});
			this.rootStore.notificationStore.success([
				'EXITSCANDETAILVIEW.TABLE.ACTION.NOTIFICATION.REMOVEFROMPALLET',
				{ name: item.fulfillmentOrder.fulfillmentOrderId },
			]);
			await this.palletDropdownStore.reload();
			this.palletDropdownStore.setValue(
				_.find(this.palletDropdownStore.options, palletOption => {
					return palletOption.value == this.palletDropdownStore.value.value;
				})
			);
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	};

	@action.bound
	defineFOTableData = () => {
		this.tableData = {
			columns: [
				{
					key: 'fulfillmentOrder.fulfillmentOrderId',
					title: 'FULFILLMENTORDER.PALLETIZINGMODE.TABLEDATA.FULFILLMENTORDERID',
					dataCell: HyperlinkCellData,
					routeName: 'master.app.main.order.list.detail',
					selected: true,
					width: 150,
					filter: null,
				},
				{
					key: 'fulfillmentOrder.fulfillmentOrderStatus.name',
					title: 'FULFILLMENTORDER.PALLETIZINGMODE.TABLEDATA.STATUS',
					selected: true,
					width: 150,
					filter: null,
				},
				{
					key: 'fulfillmentOrder.product.sku',
					title: 'FULFILLMENTORDER.PALLETIZINGMODE.TABLEDATA.PRODUCTSKU',
					selected: true,
					width: 150,
					filter: null,
				},
				{
					key: 'fulfillmentOrder.onHoldReason.name',
					title: 'FULFILLMENTORDER.PALLETIZINGMODE.TABLEDATA.ERROR',
					selected: true,
					width: 150,
					filter: null,
				},
			],
			actions: {
				onRemoveFromPallet: async item => this.openConfirmationModal(this.getRemoveFromPalletConfirmModalProps(item)),
			},
			sortObject: [
				{
					field: 'fulfillmentOrderId',
					dir: 'asc',
				},
			],
		};
		this.permissions = {
			onRemoveFromPallet: () => this.rootStore.authStore.hasPermission('fulfillmentOrderSection.full'),
		};
	};

	@action.bound
	returnToPreviousStep = async () => {
		if (this.activeStep.number > 1) {
			await this.changeStep(this.activeStep.number - 1);
		}
	};

	@action.bound
	togglePallet = () => {
		this.data = [];
		this.carrierTypeServiceTypeDropdownStore.clear();
		this.palletCreateForm.clear();
		this.palletFindNotification = null;
		this.createPalletNotification = null;
		this.createPalletMode = !this.createPalletMode;
		if (this.createPalletMode) {
			this.palletCreateForm.$('name').value = moment().toString();
			this.generateBarcode();
		}
	};

	@action.bound
	generateBarcode = () => {
		var result = '';
		const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
		const charactersLength = characters.length;
		for (var i = 0; i < 10; i++) {
			result += characters.charAt(Math.floor(Math.random() * charactersLength));
		}
		this.barcode = result;
		this.originalBarcode = result;
	};

	@action.bound
	createPallet = async () => {
		try {
			const { warehouseId, ...otherValues } = this.palletCreateForm.values();
			if (this.barcodeRef != null) {
				var label = this.barcodeRef.refs.renderElement.currentSrc.split(',')[1];
			}
			const response = await this.palletStore.create(
				{
					barcode: this.barcode,
					open: true,
					label,
					warehouseId:
						this.isSmartshipWarehouseAdministrator && _.get(this.warehouseDropdownStore, 'value')
							? this.warehouseDropdownStore.value.value
							: null,
					...otherValues,
				},
				sourceOfChange.palletizingModeCreatePallet
			);
			if (response.hasOwnProperty('statusCode')) {
				this.rootStore.notificationStore.success('FULFILLMENTORDER.PALLETIZINGMODE.NOTIFICATION.PRINTJOBCREATED');
			} else {
				this.rootStore.notificationStore.warning('FULFILLMENTORDER.PALLETIZINGMODE.NOTIFICATION.PRINTNODEINFONOTFOUND');
			}
			if (response.hasOwnProperty('pallets')) {
				this.pallet = response.pallets[0];
			} else {
				this.pallet = response[0];
			}
			this.palletCreateForm.clear();
			this.createPalletMode = false;
			this.createPalletNotification = 'FULFILLMENTORDER.PALLETIZINGMODE.NOTIFICATION.PALLET.SUCCESS';
			this.palletDropdownStore.setValue(this.pallet);
			await this.palletDropdownStore.reload();
			this.carrierTypeServiceTypeDropdownStore.clear();
		} catch (err) {
			if (err.statusCode == 409) {
				this.createPalletNotification = 'FULFILLMENTORDER.PALLETIZINGMODE.NOTIFICATION.PALLET.CONFLICT';
			} else {
				notificationError({ store: this.rootStore.notificationStore, err });
			}
		}
	};

	@action.bound
	onPrintLabel(label) {
		const fileUrl = createFileUrl(label);
		printJS(fileUrl);
	}

	@action.bound
	changeStep = async stepNumber => {
		_.forEach(this.steps, step => {
			if (step.number === stepNumber) {
				step.isActive = true;
				step.isCompleted = false;
				this.activeStep = step;
			} else {
				step.isActive = false;
				if (step.number < stepNumber) {
					step.isCompleted = true;
				}
			}
		});
		this.resetMessages();
		// UPC input is visible on steps 1 and 2 and it has to be focused for the user.
		if ((this.activeStep.number === 1 || this.activeStep.number === 2) && this.upcInput) {
			// Initially scanned UPC should be always selected on step 1.
			if (this.activeStep.number === 1) {
				if (this.isFulfillmentOrderIdEntered) {
					this.isUpcScanned = false;
					this.isFulfillmentOrderIdEntered = false;
				} else {
					this.steps.labelingStep.form.$('upc').value = this.scannedUpc;
				}
			}
			this.upcInput.focus();
		}
		if (this.activeStep.number === 3) {
			this.boxDropdownStore.reload();
		}
		// Steps 3 and 4 are the only ones showing the additional document print buttons.
		if (this.activeStep.number === 3 || this.activeStep.number === 4) {
			this.refreshAdditionalDocuments();
		}
	};

	@action.bound
	onItemScan = scannedUpc => {
		let notFoundItems = 0;
		const totalItemCount = this.selectedFulfillmentOrder.fulfillmentOrderItems.length;
		_.forEach(this.selectedFulfillmentOrder.fulfillmentOrderItems, item => {
			if (item.product.productUpcs.some(upc => upc.upc === scannedUpc)) {
				if (item.scannedQuantity < item.quantity) {
					item.scannedQuantity++;
					if (item.scannedQuantity === item.quantity) {
						// Item is marked as scanned only if all quantity of the item is scanned.
						item.isScanned = !item.isScanned;
					}
					this.soundService.success();
				} else {
					this.soundService.error();
				}
				return false; // Breaks the forEach.
			} else {
				notFoundItems++;
			}
		});
		if (notFoundItems === totalItemCount) {
			this.soundService.error();
		}
		this.closeBoxDisabled = this.isCloseBoxDisabled();
		this.closeBoxMessage = null;
	};

	@action.bound
	removeItemFromBox = productId => {
		_.forEach(this.selectedFulfillmentOrder.fulfillmentOrderItems, item => {
			if (item.product.id === productId) {
				item.scannedQuantity--;
				item.isScanned = false;
			}
		});
		this.closeBoxDisabled = this.isCloseBoxDisabled();
	};

	@action.bound
	selectFulfillmentOrder = async fulfillmentOrder => {
		this.initialFulfillmentOrder = fulfillmentOrder;
		this.splittedFulfillmentOrderIds = [];
		this.selectedFulfillmentOrder = fulfillmentOrder;
		this.activeShipmentInfo = null;

		// If FO is not in LabelCreated and it doesn't have active label proceed with printing the label or dimension step
		if ((await this.checkLabelCreatedAndChangeToFinalStep(this.selectedFulfillmentOrder)) == false) {
			this.dimensionsStepErrorMessage =
				this.activeShipmentInfo != null ? 'FULFILLMENTORDER.LABELINGMODE.MESSAGE.EXISTINGLABEL' : null;
			this.selectedFulfillmentOrder.fulfillmentOrderItems = _.map(
				this.selectedFulfillmentOrder.fulfillmentOrderItems,
				item => {
					const scannedQuantity = item.product.productUpcs.some(upc => upc.upc === this.scannedUpc) ? 1 : 0;
					return {
						...item,
						scannedQuantity: scannedQuantity,
						isScanned: item.quantity === scannedQuantity ? true : false,
					};
				}
			); // Adds scannedQuantity and isScanned property to each item and marks initally scanned item as already scanned once
			this.boxedFulfillmentOrders = [];
			this.closeBoxDisabled = this.isCloseBoxDisabled();
			await this.changeStep(2);
		}
	};

	// Split Fulfillment Order.
	@action.bound
	closeBox = async () => {
		this.suspend();
		try {
			this.nonScannedItems = _.filter(this.selectedFulfillmentOrder.fulfillmentOrderItems, item => !item.isScanned);
			// If all items are not scanned, proceed to create (split) new Fulfillment Order containing non scanned items
			if (this.nonScannedItems.length > 0) {
				let itemQuantities = [];
				_.forEach(this.nonScannedItems, item => {
					itemQuantities.push({
						id: item.id,
						nonScannedQuantity: item.quantity - item.scannedQuantity,
						productId: item.productId,
					});
				});
				this.newFulfillmentOrder = await this.fulfillmentOrderProcessingStore.splitFulfillmentOrderLabelingMode(
					{
						fulfillmentOrderId: this.selectedFulfillmentOrder.id,
						fulfillmentOrderItemQuantities: itemQuantities,
						// Lets the backend reject the split if someone else changed this FO since it was loaded.
						expectedFulfillmentOrderItems: _.map(this.selectedFulfillmentOrder.fulfillmentOrderItems, item => ({
							id: item.id,
							quantity: item.quantity,
						})),
					},
					sourceOfChange.splitFullfilmentOrderLabelingMode
				);
				// If FO is split and has the label (it was marked as never shipped and label was persisted), void the label first, then continue
				if (this.newFulfillmentOrder && this.selectedFulfillmentOrder.shipmentInfos.length > 0) {
					await this.shipmentInfoStore.voidLabel(
						{ fulfillmentOrderId: this.selectedFulfillmentOrder.id },
						0,
						sourceOfChange.voidActiveLabelLabelingMode
					);
				}
				this.splittedFulfillmentOrderIds.push(this.newFulfillmentOrder.id);
				if (
					_.some(this.newFulfillmentOrder.fulfillmentOrderItems, item => {
						return item.product.productUpcs.some(upc => upc.upc === this.scannedUpc);
					})
				) {
					this.fulfillmentOrders.push(this.newFulfillmentOrder);
				}
				this.newFulfillmentOrderCreated = true;
			}
			// A priceable LTL order picked from the step-1 list reaches the pallet step here rather than through
			// handleScannedFulfillmentOrders, so its pallets have to be (re)built for the selected order too.
			if (this.isLtlLabeling && !this.selectedFoHasUploadedBol) {
				this.populateLtlPallets();
			}
			await this.changeStep(3);
			if (!this.dimensionsStepErrorMessage) {
				await this.printPackingSlip(false);
			}
		} catch (error) {
			this.closeBoxMessage =
				error.statusCode == 400 &&
				_.get(error, 'data.message') == 'FULFILLMENTORDER.LABELINGMODE.FULFILLMENTORDERCHANGED'
					? 'FULFILLMENTORDER.LABELINGMODE.FULFILLMENTORDERCHANGED'
					: 'FULFILLMENTORDER.LABELINGMODE.SPLITORDERERROR';
		}
		this.resume();
	};

	@action.bound
	onUpcScan = async event => {
		if (event.target.name === 'upc') {
			this.steps.labelingStep.form.$('upc').value = event.currentTarget.value.trim(); // Remove whitespace from scanned UPC.
			if (event.key !== 'Enter' || event.currentTarget.value.length == 0) {
				return;
			}
			this.suspend();
			// On first UPC scan, fetch all the Fulfillment Orders associated with scanned UPC.
			if (this.activeStep.number !== 2) {
				this.scannedUpc = this.steps.labelingStep.form.$('upc').value;
				await Promise.all([this.productStore.findProduct({ upc: this.scannedUpc }), this.findFulfillmentOrders()]); // Fetch scanned Product and all Picking Fulfillment Orders associated with the Product.
				this.product = this.productStore.data[0]; // Create endpoint to fetch only one product by search criteria (UPC)?
				this.isUpcScanned = true;
				this.scanUpcMessage = null;
				// If no Fulfillment Order is found.
				if (this.findFulfillmentOrdersResponse.message) {
					this.isUpcScanned = false;
					this.scanUpcMessage = this.findFulfillmentOrdersResponse.message;
					this.scanUpcMessageData = { ids: this.findFulfillmentOrdersResponse.fulfillmentOrderIds.join(', ') };
				} else if (this.fulfillmentOrders && this.fulfillmentOrders.length === 0) {
					this.isUpcScanned = false;
					if (!this.useRegularPalletizingMode) {
						this.scanUpcMessage = 'FULFILLMENTORDER.LABELINGMODE.MESSAGE.NOTFOUNDORONHOLDWITHPALLET';
					} else {
						this.scanUpcMessage = 'FULFILLMENTORDER.LABELINGMODE.MESSAGE.NOTFOUNDORONHOLD';
					}
					this.soundService.error();
				} else {
					await this.handleScannedFulfillmentOrders();
					this.soundService.success();
				}
			}
			// On second UPC scan, mark item as scanned for selected Fulfillment Order.
			else if (
				this.activeStep.number === 2 &&
				this.selectedFulfillmentOrder &&
				(this.isUpcScanned || this.isFulfillmentOrderIdEntered)
			) {
				this.onItemScan(event.target.value);
			}
		} else if (event.target.name === 'fulfillmentOrderId') {
			this.steps.labelingStep.form.$('fulfillmentOrderId').value = event.currentTarget.value.trim(); // Remove whitespace from scanned UPC.
			if (event.key !== 'Enter' || event.currentTarget.value.length == 0) {
				return;
			}
			this.suspend();
			this.fulfillmentOrders = (await this.fulfillmentOrderStore.findFulfillmentOrder({
				page: 1,
				rpp: 100,
				statusIds: this.fulfillmentOrderStatuses
					.filter(s => [fulfillmentOrderStatus.picking, fulfillmentOrderStatus.labelCreated].includes(s.abrv))
					.map(s => s.id),
				fulfillmentOrderIds: this.steps.labelingStep.form.$('fulfillmentOrderId').value,
				isLtl: true,
				includeBillOfLading: true,
				embed: [
					'shipmentInfos',
					'shipmentInfos.fulfillmentOrder',
					'shipmentInfos.fulfillmentOrder.onHoldReason',
					'fulfillmentOrderStatus',
					'fulfillmentOrderItems',
					'fulfillmentOrderItems.product',
					'fulfillmentOrderItems.product.dimensionUnit',
					'fulfillmentOrderItems.product.weightUnit',
					'fulfillmentOrderItems.product.productUpcs',
					'fulfillmentOrderItems.orderLine',
					'order',
					'order.channel',
					'order.channel.channelType',
					'carrierService',
					'carrierService.carrierServiceType',
					'seller',
					'seller.sellerOrganizationInfo',
				],
			})).item;

			if (this.fulfillmentOrders && this.fulfillmentOrders.length > 0) {
				this.isFulfillmentOrderIdEntered = true;

				await this.handleScannedFulfillmentOrders();
			} else {
				this.rootStore.notificationStore.error('FULFILLMENTORDER.LABELINGMODE.MESSAGE.LTL.NOTFOUND');
			}
		}
		this.resume();
	};

	@action.bound
	checkAndSetActiveShipment = fulfillmentOrder => {
		if (fulfillmentOrder.shipmentInfos !== null && fulfillmentOrder.shipmentInfos.length > 0)
			for (let i = 0; i < fulfillmentOrder.shipmentInfos.length; i++) {
				if (fulfillmentOrder.shipmentInfos[i].status == shipmentInfoStatusEnum.active) {
					this.activeShipmentInfo = fulfillmentOrder.shipmentInfos[i];
					return true;
				}
			}

		return false;
	};

	@action.bound
	checkLabelCreatedAndChangeToFinalStep = async fulfillmentOrder => {
		// If FO is in LabelCreated status and it has active label
		if (
			this.checkAndSetActiveShipment(fulfillmentOrder) &&
			fulfillmentOrder.fulfillmentOrderStatus.abrv === fulfillmentOrderStatus.labelCreated
		) {
			this.steps.finalStep.form.$('trackingNumber').value = this.activeShipmentInfo.trackingNumber;
			await this.changeStep(4);
			return true;
		}
		return false;
	};

	// Priceable LTL = LTL order whose seller prices LTL and isn't Prime. These re-quote Priority1 with the
	// worker-entered pallet dimensions at dispatch time, so the Dimensions step must be shown before the BOL pull.
	isPriceableLtlOrder = fulfillmentOrder => {
		const order = fulfillmentOrder.order;
		// isLtlShipment (backend) marks LTL orders, including those assigned an LTL rate off a non-LTL channel;
		// the priceable checks narrow it to orders dispatched through Priority1.
		return (
			fulfillmentOrder.isLtlShipment &&
			!order.isPrime &&
			!!(
				fulfillmentOrder.seller &&
				fulfillmentOrder.seller.sellerOrganizationInfo &&
				fulfillmentOrder.seller.sellerOrganizationInfo.priceLtl
			)
		);
	};

	// True when the order uses the pallet-dimensions flow instead of a box/label: the LTL packing mode, or any
	// LTL order being labeled.
	@computed get isLtlLabeling() {
		return (
			this.isLtlPackingMode ||
			(this.selectedFulfillmentOrder != null && this.isPriceableLtlOrder(this.selectedFulfillmentOrder))
		);
	}

	// True when the selected order already carries a manually uploaded BOL. In that case the shipment is never
	// re-dispatched (the backend skips dispatch when a BOL exists), so we hide the pallet-dimensions input and
	// Print BOL simply prints the uploaded document.
	@computed get selectedFoHasUploadedBol() {
		const source =
			this.selectedFulfillmentOrder &&
			this.selectedFulfillmentOrder.billOfLading &&
			this.selectedFulfillmentOrder.billOfLading.source;
		return (
			source === shipmentDocumentSourceEnum.uploaded.value || source === shipmentDocumentSourceEnum.uploadedViaAPI.value
		);
	}

	// Builds one content row per order item for a pallet (every pallet lists every item; quantity 0 = not on this
	// pallet). `full` puts each item's whole quantity on the pallet (the default single pallet), else all zero.
	buildPalletContents = full => {
		const items = (this.selectedFulfillmentOrder && this.selectedFulfillmentOrder.fulfillmentOrderItems) || [];
		return items.map(item => ({
			fulfillmentOrderItemId: item.id,
			sku: item.sku,
			productName: item.product ? item.product.name : '',
			quantity: full ? item.quantity : 0,
		}));
	};

	// Product weights are stored in the seller's own units, but the pallet weight is pounds: the backend sends whatever
	// is entered here to Priority1 without converting. Same factor the backend DimensionConverter uses.
	toPounds = product => {
		const value = (product && product.weight) || 0;
		return _.get(product, 'weightUnit.name') === 'Kilograms' ? value * 2.2046 : value;
	};

	// Sum of (product unit weight x units placed) across a pallet's contents.
	calcPalletContentsWeight = contents => {
		const items = (this.selectedFulfillmentOrder && this.selectedFulfillmentOrder.fulfillmentOrderItems) || [];
		const total = (contents || []).reduce((sum, c) => {
			const item = _.find(items, i => i.id === c.fulfillmentOrderItemId);
			return sum + this.toPounds(item && item.product) * (Number(c.quantity) || 0);
		}, 0);
		return _.round(total, 2);
	};

	// Builds an empty, open pallet listing every order item at quantity 0. Dimensions are left blank so the worker has to
	// measure them.
	buildPallet = () => {
		const contents = this.buildPalletContents(false);
		return {
			palletLength: '',
			palletWidth: '',
			palletHeight: '',
			palletWeight: this.calcPalletContentsWeight(contents),
			palletCount: 1,
			isStackable: false,
			weightOverridden: false,
			isClosed: false,
			contents,
		};
	};

	// Starts the LTL Dimensions step with one empty, open pallet the worker fills by scanning. The pallet layout
	// estimated at rate-assign time is deliberately not pre-filled: a pre-reconciled layout let the BOL be printed
	// without anyone checking what was actually on the pallets.
	@action.bound
	populateLtlPallets = () => {
		const pallets = this.steps.dimensionsStep.form.$('pallets');
		// Delete by field rather than by index: keys are not reindexed on del(), so they become sparse.
		pallets.map(pallet => pallet).forEach(pallet => pallet.del());
		pallets.add([this.buildPallet()]);
		this.steps.dimensionsStep.form.$('palletUpc').clear();
		this.palletScanMessage = null;
	};

	@action.bound
	addPallet = () => {
		this.steps.dimensionsStep.form.$('pallets').add([this.buildPallet()]);
	};

	@computed get openPallet() {
		if (!this.steps || !this.steps.dimensionsStep) return null;
		const open = this.steps.dimensionsStep.form
			.$('pallets')
			.map(pallet => pallet)
			.filter(pallet => pallet.$('isClosed').value !== true);
		return open.length > 0 ? open[0] : null;
	}

	palletUnitCount = palletField =>
		palletField
			.$('contents')
			.map(content => Number(content.$('quantity').value) || 0)
			.reduce((sum, quantity) => sum + quantity, 0);

	// The values are checked directly because isValid stays true on pallets added after form construction until the
	// field has been validated, so a blank height would otherwise pass.
	isPalletReadyToClose = palletField =>
		this.palletUnitCount(palletField) > 0 &&
		['palletLength', 'palletWidth', 'palletHeight', 'palletWeight'].every(
			name => Number(palletField.$(name).value) > 0 && palletField.$(name).isValid
		);

	// Refocuses the scan input after React re-renders: it may still be disabled, or about to lose focus to an
	// unmounting button, when the action runs.
	focusPalletUpcInput = () => {
		setTimeout(() => {
			if (this.palletUpcInput) this.palletUpcInput.focus();
		});
	};

	// One scan adds one unit of the matching order line to the open pallet. When the same UPC is on several lines,
	// the first line that still has units left wins.
	@action.bound
	onPalletUpcScan = event => {
		if (event.key !== 'Enter') return;
		const upc = (event.currentTarget.value || '').trim();
		this.steps.dimensionsStep.form.$('palletUpc').clear();
		if (!upc) return;

		const pallet = this.openPallet;
		if (!pallet) {
			this.palletScanMessage = 'FULFILLMENTORDER.LABELINGMODE.MESSAGE.LTL.NOOPENPALLET';
			this.soundService.error();
			return;
		}

		const matches = _.filter(this.selectedFulfillmentOrder.fulfillmentOrderItems, item =>
			_.some(_.get(item, 'product.productUpcs') || [], productUpc => productUpc.upc === upc)
		);
		if (matches.length === 0) {
			this.palletScanMessage = 'FULFILLMENTORDER.LABELINGMODE.MESSAGE.LTL.UPCNOTONORDER';
			this.soundService.error();
			return;
		}

		const item = _.find(matches, match => (this.remainingByItem[match.id] || 0) > 0);
		if (!item) {
			this.palletScanMessage = 'FULFILLMENTORDER.LABELINGMODE.MESSAGE.LTL.ALLUNITSPLACED';
			this.soundService.error();
			return;
		}

		const content = pallet
			.$('contents')
			.map(c => c)
			.find(c => c.$('fulfillmentOrderItemId').value === item.id);
		if (!content) {
			this.palletScanMessage = 'FULFILLMENTORDER.LABELINGMODE.MESSAGE.LTL.UPCNOTONORDER';
			this.soundService.error();
			return;
		}
		this.incrementContent(pallet, content);
		this.palletScanMessage = null;
		this.soundService.success();
	};

	// Locks the open pallet and, while units are still unplaced, opens the next one.
	@action.bound
	closePallet = () => {
		const pallet = this.openPallet;
		if (!pallet || !this.isPalletReadyToClose(pallet)) return;
		pallet.$('isClosed').set(true);
		if (_.some(this.palletReconciliation, row => row.left > 0)) {
			this.addPallet();
		}
		this.palletScanMessage = null;
		this.focusPalletUpcInput();
	};

	// Only one pallet may be open: an empty open pallet is discarded, one holding units must be closed first.
	@action.bound
	reopenPallet = palletField => {
		const open = this.openPallet;
		if (open) {
			if (this.palletUnitCount(open) > 0) {
				this.palletScanMessage = 'FULFILLMENTORDER.LABELINGMODE.MESSAGE.LTL.CLOSECURRENTPALLETFIRST';
				this.soundService.error();
				return;
			}
			open.del();
		}
		palletField.$('isClosed').set(false);
		this.palletScanMessage = null;
		this.focusPalletUpcInput();
	};

	@action.bound
	unscanContent = (palletField, contentField) => {
		this.decrementContent(palletField, contentField);
		this.focusPalletUpcInput();
	};

	// Removes a pallet, keeping at least one. Its units return to the "left" pool; if that leaves no open pallet to
	// scan them onto, a new one is opened.
	@action.bound
	removePallet = palletField => {
		const pallets = this.steps.dimensionsStep.form.$('pallets');
		if (pallets.value.length <= 1) return;
		palletField.del();
		if (!this.openPallet && _.some(this.palletReconciliation, row => row.left > 0)) {
			this.addPallet();
		}
		this.focusPalletUpcInput();
	};

	// Sets a content's quantity clamped to [0, current + units still left for that product], then recalculates the
	// pallet weight. This keeps the per-product total from ever exceeding the order quantity.
	@action.bound
	setContentQuantity = (palletField, contentField, value) => {
		const current = Number(contentField.$('quantity').value) || 0;
		const id = contentField.$('fulfillmentOrderItemId').value;
		const max = current + (this.remainingByItem[id] || 0);
		const next = _.clamp(Number.isFinite(value) ? value : 0, 0, max);
		contentField.$('quantity').set(next);
		this.recalcPalletWeight(palletField);
	};

	@action.bound
	incrementContent = (palletField, contentField) => {
		this.setContentQuantity(palletField, contentField, (Number(contentField.$('quantity').value) || 0) + 1);
	};

	@action.bound
	decrementContent = (palletField, contentField) => {
		this.setContentQuantity(palletField, contentField, (Number(contentField.$('quantity').value) || 0) - 1);
	};

	// Recomputes a pallet's weight from its contents unless the worker has manually overridden it.
	@action.bound
	recalcPalletWeight = palletField => {
		if (palletField.$('weightOverridden').value) return;
		palletField.$('palletWeight').set(this.calcPalletContentsWeight(palletField.$('contents').values()));
	};

	// Marks a pallet's weight as manually edited so subsequent stepper changes no longer overwrite it.
	@action.bound
	markWeightOverridden = palletField => {
		palletField.$('weightOverridden').set(true);
	};

	// Per-product reconciliation for the LTL Dimensions step: units still "left" to assign (order quantity minus what
	// is placed across all pallets). Every product must be fully placed (0 left) before the BOL can be printed.
	@computed get palletReconciliation() {
		const items = (this.selectedFulfillmentOrder && this.selectedFulfillmentOrder.fulfillmentOrderItems) || [];
		const pallets = (this.steps.dimensionsStep && this.steps.dimensionsStep.form.$('pallets').value) || [];
		return items.map(item => {
			let assigned = 0;
			pallets.forEach(pallet =>
				(pallet.contents || []).forEach(content => {
					if (content.fulfillmentOrderItemId === item.id) {
						assigned += Number(content.quantity) || 0;
					}
				})
			);
			return {
				fulfillmentOrderItemId: item.id,
				sku: item.sku,
				productName: item.product ? item.product.name : '',
				required: item.quantity,
				assigned,
				left: item.quantity - assigned,
				ok: item.quantity - assigned === 0,
			};
		});
	}

	// Units still left to assign, keyed by fulfillment order item id (drives the "N left" header and the +/- caps).
	@computed get remainingByItem() {
		const map = {};
		this.palletReconciliation.forEach(row => {
			map[row.fulfillmentOrderItemId] = row.left;
		});
		return map;
	}

	// True when every product is fully placed (0 left), every pallet holds at least one unit and every pallet is closed.
	@computed get isLtlPalletsReconciled() {
		const pallets = (this.steps.dimensionsStep && this.steps.dimensionsStep.form.$('pallets').value) || [];
		const noEmptyPallet =
			pallets.length > 0 &&
			pallets.every(p => (p.contents || []).reduce((s, c) => s + (Number(c.quantity) || 0), 0) > 0);
		const allClosed = pallets.every(p => p.isClosed === true);
		return this.palletReconciliation.every(row => row.ok) && noEmptyPallet && allClosed;
	}

	@action.bound
	handleScannedFulfillmentOrders = async () => {
		this.suspend();
		let isLtlPriceable = this.isPriceableLtlOrder(this.fulfillmentOrders[0]);

		// Sorting is done on the backend. First in line are single item Fulfillment orders.
		if (
			isLtlPriceable ||
			this.fulfillmentOrders[0].order.requiredCarrierService === 'LTL' ||
			this.fulfillmentOrders[0].order.channel.name === 'LTL Freight' ||
			(this.fulfillmentOrders[0].fulfillmentOrderItems.length === 1 &&
				this.fulfillmentOrders[0].fulfillmentOrderItems[0].quantity === 1)
		) {
			this.selectiongFoStepDisabled = true;
			this.selectedFulfillmentOrder = this.fulfillmentOrders[0];
			this.initialFulfillmentOrder = this.selectedFulfillmentOrder;

			// If FO is not in LabelCreated and it doesn't have active label proceed with printing the label
			if ((await this.checkLabelCreatedAndChangeToFinalStep(this.selectedFulfillmentOrder)) == false) {
				// Priceable LTL must capture the actual pallet dimensions (which drive the re-quote + BOL dispatch)
				// before the label is pulled, so stop at the Dimensions step instead of dispatching immediately.
				// When an uploaded BOL already exists there is nothing to dispatch, so skip the pallet-dimensions
				// rows (the input is hidden) and just let the worker print the uploaded BOL from the same step.
				if (isLtlPriceable) {
					if (!this.selectedFoHasUploadedBol) {
						this.populateLtlPallets();
					}
					await this.changeStep(3);
				} else {
					await this.printLabel();
				}
			}
		}
		// If all Fulfillment Orders in the list are multi items, then proceed to step 1 (Selecting Fo step).
		else if (
			(_.every(this.fulfillmentOrders, fulfillmentOrder => {
				return fulfillmentOrder.fulfillmentOrderItems.length > 1;
			}) ||
				_.some(this.fulfillmentOrders, fulfillmentOrder => {
					return _.some(fulfillmentOrder.fulfillmentOrderItems, item => {
						return item.quantity > 1;
					});
				})) &&
			this.fulfillmentOrders.length > 1
		) {
			this.selectiongFoStepDisabled = false;
			await this.changeStep(1);
		}
		// Else, pick the first from the list. This is usually when only one Fulfillment Order is in the list.
		else {
			this.selectiongFoStepDisabled = true;
			this.selectFulfillmentOrder(this.fulfillmentOrders[0]);
		}
		this.resume();
	};

	@action.bound
	onTrackingNumberScan = event => {
		const regex = /^[0-9\b]+$/;
		this.createLabelMessage = null;
		if (this.clearInputOnChange && !event.ctrlKey && regex.test(event.key)) {
			this.clearInputOnChange = false;
			this.steps.finalStep.form.$('trackingNumber').value = event.currentTarget.value.slice(-1);
		} else {
			const valueWithoutWhitespace = event.currentTarget.value.replace(/ /g, '');
			this.steps.finalStep.form.$('trackingNumber').value = valueWithoutWhitespace;
		}

		if (event.key === 'Enter' && event.currentTarget.value.length != 0) {
			if (this.activeStep.number === 4 && this.steps.finalStep.form.$('trackingNumber').value) {
				this.onScanTrackingNumber();
			}
		}
	};

	@action.bound
	findFulfillmentOrders = async () => {
		try {
			const params = {
				carrierTypeId: !this.useRegularPalletizingMode
					? this.palletDropdownStore.value.extra.carrierTypeServiceType.carrierTypeId
					: null,
				carrierServiceTypeId: !this.useRegularPalletizingMode
					? this.palletDropdownStore.value.extra.carrierTypeServiceType.carrierServiceTypeId
					: null,
				warehouseIds: _.get(this.warehouseDropdownStore, 'value.value')
					? [this.warehouseDropdownStore.value.value]
					: null,
				isLtl: false,
			};
			this.findFulfillmentOrdersResponse = await this.fulfillmentOrderStore.getFulfillmentOrdersForLabeling({
				page: 1,
				rpp: 10, // Set records per page based on modal page size. This is future feature. Max no. of Fulfillment orders on one UPC scan is 10.
				upc: this.steps.labelingStep.form.$('upc').value,
				embed: [
					'shipmentInfos',
					'shipmentInfos.fulfillmentOrder',
					'shipmentInfos.fulfillmentOrder.onHoldReason',
					'fulfillmentOrderStatus',
					'fulfillmentOrderItems',
					'fulfillmentOrderItems.product',
					'fulfillmentOrderItems.product.dimensionUnit',
					'fulfillmentOrderItems.product.weightUnit',
					'fulfillmentOrderItems.product.productUpcs',
					'fulfillmentOrderItems.orderLine',
					'order',
					'order.channel',
					'order.channel.channelType',
					'carrierService',
					'carrierService.carrierServiceType',
					'seller',
					'seller.sellerOrganizationInfo',
				],
				...params,
			});
			this.fulfillmentOrders = this.findFulfillmentOrdersResponse.item;
		} catch (error) {
			notificationError({ store: this.rootStore.notificationStore, error });
		}
	};

	@action.bound
	onScanTrackingNumber = async () => {
		try {
			this.isUndoClicked = false;
			const { trackingNumber } = this.steps.finalStep.form.values();
			let params = {};
			if (this.useRegularPalletizingMode) {
				params = {
					palletId: null,
					isRegularPalletizing: true,
					sourceOfChange: sourceOfChange.scanLabel,
				};
			} else {
				params = {
					palletId: this.palletDropdownStore.value.value,
					isRegularPalletizing: false,
					sourceOfChange: sourceOfChange.palletizingModeUpdateShipmentInfoByTrackingNumber,
				};
			}
			const response = await this.shipmentInfoStore.scanTrackingNumber({
				fulfillmentOrderId: this.selectedFulfillmentOrder ? this.selectedFulfillmentOrder.id : null,
				trackingNumber:
					trackingNumber.length === 34 ? trackingNumber.substring(trackingNumber.length - 12) : trackingNumber,
				...params,
			});
			if (response.errorMessage == null) {
				await this.soundService.success();
				this.selectedFulfillmentOrder = response.entity.fulfillmentOrder;
				this.scanLabelMessage = null;
				this.lastAction = 'onTrackingNumberScan';
				this.actionsDone.push(this.lastAction);
				// If selected Fulfillment Order is splitted, add it to boxedFulfillmentOrders list, and select newly created Fulfillment Order as selected for further processing.
				if (this.nonScannedItems.length > 0) {
					_.remove(this.fulfillmentOrders, fulfillmentOrder => {
						return fulfillmentOrder.id === this.selectedFulfillmentOrder.id;
					});
					this.boxedFulfillmentOrders.push(this.selectedFulfillmentOrder);
					this.packagedFulfillmentOrders.push(this.selectedFulfillmentOrder);
					this.timeoutAction = 'stepTwo';
				}
				// Else if there is more multi items left for processing, add the latest one to packagedFulfillmentOrders list and return to step 1 where user will pick another multi item.
				else if (!this.selectiongFoStepDisabled) {
					_.remove(this.fulfillmentOrders, fulfillmentOrder => {
						return fulfillmentOrder.id === this.selectedFulfillmentOrder.id;
					});
					this.boxedFulfillmentOrders = [];
					this.packagedFulfillmentOrders.push(this.selectedFulfillmentOrder);
					this.steps.labelingStep.form.$('upc').value = this.scannedUpc;
					this.timeoutAction = 'stepOne';
				}
				// Else return to UPC scan.
				else {
					this.timeoutAction = 'stepZero';
				}
				await this.actionAfterTimeout();
				this.scanLabelMessage = null;
				this.createLabelMessage = null;
				if (!this.useRegularPalletizingMode) {
					await this.palletDropdownStore.reload();
				}
			} else {
				this.scanLabelMessage = response.errorMessage;
				await this.soundService.error();
			}
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
	};

	@action.bound
	printLabel = async (ltlRateDecision = {}) => {
		this.isUndoClicked = false;
		this.newFulfillmentOrderCreated = false;
		const { boxId } = this.steps.dimensionsStep.form.values();
		try {
			this.suspend();
			await this.shipmentInfoStore.createOrGetShipment(
				{
					fulfillmentOrderId: this.selectedFulfillmentOrder.id,
					boxId: boxId,
					// Priceable LTL: the actual per-product palletized-freight dimensions the worker entered drive the
					// re-quote + dispatch (one entry per fulfillment order item). Skipped when an uploaded BOL already
					// exists - there are no pallet rows and nothing is dispatched, we only reprint the uploaded BOL.
					...(this.isPriceableLtlOrder(this.selectedFulfillmentOrder) &&
						!this.selectedFoHasUploadedBol && {
							// Send only what is actually stacked: drop zero-quantity contents and any empty pallet.
							pallets: this.steps.dimensionsStep.form
								.$('pallets')
								.values()
								.map(p => ({
									...p,
									contents: (p.contents || []).filter(c => (Number(c.quantity) || 0) > 0),
								}))
								.filter(p => p.contents.length > 0),
						}),
					// The worker's answer to the rate-changed prompt: acceptedLtlRate re-dispatches at the accepted
					// price, holdLtl puts the order on hold instead.
					...ltlRateDecision,
				},
				sourceOfChange.labelingModeCreateOrGetShipment
			);
			// Priceable LTL: the dispatch-time re-quote priced the selected carrier/service differently. Nothing was
			// dispatched or held yet - ask the worker to book the new rate or put the order on hold.
			if (this.shipmentInfoStore.response.requiresLtlRateConfirmation) {
				this.openLtlRateChangedModal(
					this.shipmentInfoStore.response.originalLtlRate,
					this.shipmentInfoStore.response.newLtlRate
				);
				this.resume();
				return;
			}
			this.lastAction = 'printLabel';
			this.actionsDone.push(this.lastAction);
			this.modalParams.onClose = this.modalParams.data.printLabelCallback;
			if (this.shipmentInfoStore.response.shipmentInfo != null) {
				if (this.shipmentInfoStore.response.shipmentInfo.trackingNumber) {
					this.steps.finalStep.form.$(
						'trackingNumber'
					).value = this.shipmentInfoStore.response.shipmentInfo.trackingNumber.toString();
				}
				this.selectedFulfillmentOrder = this.shipmentInfoStore.response.fulfillmentOrder;

				// Priority1 LTL: past the same-day cutoff (or on a non-working day) the pickup was booked for a later
				// day. Sticky so the worker does not miss that the truck is not coming today.
				if (this.shipmentInfoStore.response.movedLtlPickupDate) {
					this.rootStore.notificationStore.warning(
						[
							'FULFILLMENTORDER.LABELINGMODE.MESSAGE.LTLPICKUPMOVED',
							{
								id: this.selectedFulfillmentOrder.fulfillmentOrderId,
								date: moment(this.shipmentInfoStore.response.movedLtlPickupDate, 'YYYY-MM-DD').format(
									'dddd, MM/DD/YYYY'
								),
							},
						],
						false,
						{ autoClose: false }
					);
				}

				if (this.shipmentInfoStore.response.printNodeResponse === null) {
					this.rootStore.notificationStore.warning('FULFILLMENTORDER.LABELINGMODE.MESSAGE.LABEL.CREATEDANDNOTFOUND');
					this.fileURL = createFileUrl(this.shipmentInfoStore.response.shipmentInfo.label);
					setTimeout(async () => {
						await this.printDocument(this.fileURL, 'printJS-label');
						await this.printPalletPlacard(this.shipmentInfoStore.response);
						await this.printPackingSlip(false);
						await this.printUpcLabel(this.shipmentInfoStore.response);
						await this.printGs1Label(this.shipmentInfoStore.response);
						if (this.isLtlPackingMode) {
							// The packing slip is printed off this chain and needs selectedFulfillmentOrder, so the
							// LTL-packing reset is deferred to here (see the isLtlPackingMode block below).
							this.returnToLtlPackingStart();
						}
					}, 250);
				} else {
					if (this.shipmentInfoStore.response.printNodeResponse.isSuccess) {
						this.rootStore.notificationStore.success('FULFILLMENTORDER.LABELINGMODE.MESSAGE.LABEL.CREATED');
					} else {
						this.rootStore.notificationStore.error(
							'FULFILLMENTORDER.LABELINGMODE.MESSAGE.LABEL.CREATEDANDUNAUTHORIZED'
						);
					}
					await this.printPalletPlacard(this.shipmentInfoStore.response);
					await this.printPackingSlipFromResponse(this.shipmentInfoStore.response);
					await this.printUpcLabel(this.shipmentInfoStore.response);
					await this.printGs1Label(this.shipmentInfoStore.response);
				}
				if (this.isLtlPackingMode) {
					// Printing the BOL is the last step of the LTL packing workflow: the order is palletized (status
					// set on the backend). Return to the FO-id entry screen so the next order can be processed.
					// When the label printed client-side (no PrintNode), the packing slip is printed asynchronously
					// from the label's print-dialog-close callback above, which still needs selectedFulfillmentOrder -
					// so in that case the reset is deferred to that callback. Otherwise reset now.
					if (this.shipmentInfoStore.response.printNodeResponse !== null) {
						this.returnToLtlPackingStart();
					}
				} else {
					await this.changeStep(4);
					if (this.selectedFulfillmentOrder.fulfillmentOrderStatus.abrv == fulfillmentOrderStatus.exitScan) {
						this.timeoutAction = 'stepZero';
						await this.actionAfterTimeout();
					}
				}
			} else {
				// Priceable LTL dispatch failed (no shipmentInfo): the backend held the order and returned the
				// Priority1 reason. Stay on the Dimensions step so the worker can adjust dimensions and retry.
				// Everything else (regular LTL, non-LTL) advances to the Final step, as it did before SSDV-3399.
				if (!this.isPriceableLtlOrder(this.selectedFulfillmentOrder)) {
					await this.changeStep(4);
				}
				if (this.shipmentInfoStore.response.shipmentData != null) {
					if (
						this.shipmentInfoStore.response.shipmentData.failureNotification &&
						this.shipmentInfoStore.response.shipmentData.failureNotification.length > 0
					) {
						this.createLabelMessage = this.shipmentInfoStore.response.shipmentData.failureNotification[0];
					} else if (
						this.shipmentInfoStore.response.shipmentData.notifications &&
						this.shipmentInfoStore.response.shipmentData.notifications.length > 0
					) {
						this.createLabelMessage = this.shipmentInfoStore.response.shipmentData.notifications[0];
					}
				} else if (this.shipmentInfoStore.response.message != null) {
					if (this.shipmentInfoStore.response.errors) {
						const errors = _.map(this.shipmentInfoStore.response.errors, error => {
							return translate(error);
						});
						this.createLabelMessage = `${translate(this.shipmentInfoStore.response.message)} ${
							errors ? errors.join(' ') : null
						}`;
					} else {
						this.createLabelMessage = this.shipmentInfoStore.response.message;
					}
				}
				await this.soundService.error();
			}
		} catch (err) {
			if (
				err.data != null &&
				err.data.error != null &&
				err.data.error.InnerExceptions != null &&
				err.data.error.InnerExceptions.length > 0 &&
				err.data.error.InnerExceptions[0].Message
			) {
				this.createLabelMessage = err.data.error.InnerExceptions[0].Message;
			} else {
				this.createLabelMessage = 'FULFILLMENTORDER.LABELINGMODE.MESSAGE.NOTFOUNDORONHOLD';
			}
			await this.soundService.error();
		}
		this.resume();
	};

	/**
	 * Prints one document and resolves once the worker has dismissed its print dialog, so the documents of one
	 * fulfillment order are printed one after the other. Two reasons the next document cannot just be fired off:
	 * print-js keeps a single iframe per frameId and removes any frame already carrying it (each document therefore
	 * gets its own), and the browser silently ignores a print call made while another print dialog is still open -
	 * print() returns immediately, so only the dialog-close hook says the printer is free.
	 */
	printDocument = (fileUrl, frameId) =>
		new Promise(resolve => {
			// A document that never reaches the print call has no dialog to close, so that wait alone is capped -
			// the remaining documents still have to be printed.
			let printed = false;
			const timeout = setTimeout(() => {
				if (!printed) {
					resolve();
				}
			}, printDocumentTimeout);
			printJS({
				printable: fileUrl,
				frameId: frameId,
				onLoadingEnd: () => {
					printed = true;
					clearTimeout(timeout);
				},
				onPrintDialogClose: resolve,
			});
		});

	/**
	 * The packing slip rides along on the create-or-get-shipment response, which pushed it to the packing slip
	 * printer. That printer is configured separately from the label printer, so the push can be missing even when
	 * the label was printed with PrintNode - in that case the content comes back and has to be printed here.
	 */
	@action.bound
	printPackingSlipFromResponse = async response => {
		if (!response || _.get(response, 'packingSlipPrintNodeResponse.isSuccess') || !response.packingSlipContent) {
			return;
		}

		await this.printDocument(createFileUrl(response.packingSlipContent), 'printJS-packing-slip');
		this.rootStore.notificationStore.warning('FULFILLMENTORDER.LABELINGMODE.MESSAGE.PACKINGSLIP.CREATEDANDNOTFOUND');
	};

	/**
	 * The UPC label rides along on the create-or-get-shipment response: the backend already pushed it to the
	 * dedicated UPC printer, and only hands back content when that push did not happen.
	 */
	@action.bound
	printUpcLabel = async response => {
		if (!response) {
			return;
		}

		if (_.get(response, 'upcLabelPrintNodeResponse.isSuccess')) {
			this.rootStore.notificationStore.success('FULFILLMENTORDER.LABELINGMODE.MESSAGE.UPCLABEL.CREATED');
		} else if (response.upcLabelContent) {
			await this.printDocument(createFileUrl(response.upcLabelContent), 'printJS-upc-label');
			this.rootStore.notificationStore.warning('FULFILLMENTORDER.LABELINGMODE.MESSAGE.UPCLABEL.CREATEDANDNOTFOUND');
		}
	};

	/** Same flow as printUpcLabel; the backend sends the GS1 label to the UPC printer. */
	@action.bound
	printGs1Label = async response => {
		if (!response) {
			return;
		}

		if (_.get(response, 'gs1LabelPrintNodeResponse.isSuccess')) {
			this.rootStore.notificationStore.success('FULFILLMENTORDER.LABELINGMODE.MESSAGE.GS1LABEL.CREATED');
		} else if (response.gs1LabelContent) {
			await this.printDocument(createFileUrl(response.gs1LabelContent), 'printJS-gs1-label');
			this.rootStore.notificationStore.warning('FULFILLMENTORDER.LABELINGMODE.MESSAGE.GS1LABEL.CREATEDANDNOTFOUND');
		}
	};

	/** Same flow as printUpcLabel; the backend sends the placards (one page per LTL pallet) to the label printer. */
	@action.bound
	printPalletPlacard = async response => {
		if (!response) {
			return;
		}

		if (_.get(response, 'palletPlacardPrintNodeResponse.isSuccess')) {
			this.rootStore.notificationStore.success('FULFILLMENTORDER.LABELINGMODE.MESSAGE.PALLETPLACARD.CREATED');
		} else if (response.palletPlacardContent) {
			await this.printDocument(createFileUrl(response.palletPlacardContent), 'printJS-pallet-placard');
			this.rootStore.notificationStore.warning(
				'FULFILLMENTORDER.LABELINGMODE.MESSAGE.PALLETPLACARD.CREATEDANDNOTFOUND'
			);
		}
	};

	/**
	 * The packing slip, UPC label and GS1 label print buttons only exist when their document does. The flags are
	 * refreshed on every entry to the Dimensions and Palletizing steps rather than read off the loaded order,
	 * because a SellerCloud channel's documents are pulled and stored at label-creation time - an order with
	 * nothing to print on step 3 can have documents on step 4. Deliberately not awaited by the caller and
	 * deliberately silent: the scan loop must not wait on it, and a failed check must not raise a toast on every
	 * box close.
	 */
	@action.bound
	refreshAdditionalDocuments = async () => {
		// Start hidden so a previous order's documents never leave buttons on screen while the check is in flight.
		this.setAdditionalDocuments({});
		const fulfillmentOrderId = _.get(this.selectedFulfillmentOrder, 'id');
		if (!fulfillmentOrderId) {
			return;
		}
		try {
			const response = await this.fulfillmentOrderStore.getAdditionalDocuments(fulfillmentOrderId);
			// The order may have moved on while the check was in flight; only apply it to the same order.
			if (_.get(this.selectedFulfillmentOrder, 'id') === fulfillmentOrderId) {
				this.setAdditionalDocuments(response);
			}
		} catch (err) {
			this.setAdditionalDocuments({});
		}
	};

	@action.bound
	setAdditionalDocuments = ({ hasPackingSlip, hasUpcLabel, hasGs1Label }) => {
		this.hasPackingSlip = hasPackingSlip === true;
		this.hasUpcLabel = hasUpcLabel === true;
		this.hasGs1Label = hasGs1Label === true;
	};

	/** Manual reprint of a stored UPC or GS1 label; the backend sends both to the UPC printer. */
	@action.bound
	printStoredLabel = async (type, frameId, messageKey) => {
		this.suspend();
		try {
			const response = await this.shipmentDocumentStore.printDocument(this.selectedFulfillmentOrder.id, type);
			if (response.printedWithPrintNode) {
				this.rootStore.notificationStore.success(`FULFILLMENTORDER.LABELINGMODE.MESSAGE.${messageKey}.CREATED`);
			} else if (response.content) {
				await this.printDocument(createFileUrl(response.content), frameId);
				this.rootStore.notificationStore.warning(
					`FULFILLMENTORDER.LABELINGMODE.MESSAGE.${messageKey}.CREATEDANDNOTFOUND`
				);
			}
		} catch (err) {
			this.rootStore.notificationStore.error([`FULFILLMENTORDER.${messageKey}.ERROR`]);
		}
		this.resume();
	};

	@action.bound
	printStoredUpcLabel = () => this.printStoredLabel(shipmentDocumentTypeEnum.upcLabel, 'printJS-upc-label', 'UPCLABEL');

	@action.bound
	printStoredGs1Label = () => this.printStoredLabel(shipmentDocumentTypeEnum.gs1Label, 'printJS-gs1-label', 'GS1LABEL');

	@action.bound
	printPackingSlip = async isManual => {
		this.suspend();
		try {
			const pdfResponse = await this.packingSlipStore.generatePdf(this.selectedFulfillmentOrder.id, isManual);
			this.shouldPrintPackingSlip = pdfResponse.shouldPrintPackingSlip;
			if (!pdfResponse.packingSlip) {
				this.hasPackingSlip = false;
				// The auto path runs after every box close, so only a deliberate click is told there is nothing to print.
				if (isManual) {
					this.rootStore.notificationStore.warning('FULFILLMENTORDER.LABELINGMODE.MESSAGE.PACKINGSLIP.NOTFOUND');
				}
			} else if (pdfResponse.printedWithPrintNode) {
				this.rootStore.notificationStore.success('FULFILLMENTORDER.LABELINGMODE.MESSAGE.PACKINGSLIP.CREATED');
			} else if (this.shouldPrintPackingSlip) {
				const fileURL = createFileUrl(pdfResponse.packingSlip);
				await this.printDocument(fileURL, 'printJS-packing-slip');
				this.rootStore.notificationStore.warning(
					'FULFILLMENTORDER.LABELINGMODE.MESSAGE.PACKINGSLIP.CREATEDANDNOTFOUND'
				);
			}
		} catch (err) {
			this.rootStore.notificationStore.error(['FULFILLMENTORDER.LABELINGMODE.PACKINGSLIP.ERROR']);
		}
		this.resume();
	};

	getRemoveItemConfirmModalProps = productId => {
		return {
			onConfirm: async () => this.removeItemFromBox(this.rootStore.viewStore.selectedConfirmModal.data),
			message: ['FULFILLMENTORDER.LABELINGMODE.MESSAGE.REMOVEITEMCONFIRMATION'],
			data: productId,
		};
	};

	@action.bound
	onBoxChange = value => {
		if (_.get(value, 'label') == 'Add new template') {
			this.showCreateTemplate = true;
			this.steps.dimensionsStep.form.$('boxId').resetValidation();
			this.steps.dimensionsStep.form.$('boxId').set('rules', '');
		} else {
			this.showCreateTemplate = false;
			this.steps.dimensionsStep.form.$('boxId').set('rules', 'required');
		}
		this.resetMessages();
	};

	@action.bound
	onCreateTemplateCancel = () => {
		this.steps.dimensionsStep.form.clear();
		this.steps.dimensionsStep.form.$('boxId').invalidate();
		this.boxDropdownStore.clear();
		this.showCreateTemplate = false;
		this.steps.dimensionsStep.form.$('boxId').set('rules', 'required');
	};

	@action.bound
	onCreateTemplateSubmit = async () => {
		try {
			const { name, width, height, length } = this.steps.dimensionsStep.form.values();
			const boxValues = {
				name,
				width,
				height,
				length,
				organizationId: this.isSuperAdminOrganization ? this.selectedFulfillmentOrder.sellerId : null,
			};
			const response = await this.lookupStore.createLookup(boxValues, lookupRouteName.box, sourceOfChange.createLookup);
			if (response.message) {
				this.steps.dimensionsStep.form.$('name').invalidate(response.message);
			} else {
				var createdBox = this.lookupStore.item;
				if (createdBox) {
					this.showCreateTemplate = false;
					this.steps.dimensionsStep.form.$('name').clear();
					this.steps.dimensionsStep.form.$('height').clear();
					this.steps.dimensionsStep.form.$('width').clear();
					this.steps.dimensionsStep.form.$('length').clear();
					this.boxDropdownStore.reload();
					this.boxDropdownStore.setValue(createdBox);
					this.steps.dimensionsStep.form.$('boxId').set('rules', 'required');
				}
			}
		} catch (err) {}
	};

	@action.bound
	async onUndo() {
		const lastActionDone = _.last(this.actionsDone);
		this.isUndoClicked = true;

		try {
			// Undo from Label Created status to Picking.
			if (lastActionDone === 'printLabel') {
				const fulfillmentOrder = await this.shipmentInfoStore.undoLabelCreated(
					{ fulfillmentOrderId: this.selectedFulfillmentOrder.id },
					sourceOfChange.undoPrintLabelLabelingMode
				);
				this.selectedFulfillmentOrder.fulfillmentOrderStatus = fulfillmentOrder.fulfillmentOrderStatus;
				this.packagedFulfillmentOrders.pop();
				this.removeElementFromActionsList();
				this.lastAction = null;
				if (
					// If single item, return to UPC scan because step 1, 2 and 3 were skipped
					this.fulfillmentOrders[0].order.requiredCarrierService === 'LTL' ||
					this.fulfillmentOrders[0].order.channel.name === 'LTL Freight' ||
					(this.fulfillmentOrders[0].fulfillmentOrderItems.length === 1 &&
						this.fulfillmentOrders[0].fulfillmentOrderItems[0].quantity === 1)
				) {
					this.isUpcScanned = false;
					this.isFulfillmentOrderIdEntered = false;
					this.resetSteps();
				} else {
					await this.returnToPreviousStep();
				}
			}
			// Undo from Packaged status to Label Created.
			else if (lastActionDone === 'onTrackingNumberScan') {
				const fulfillmentOrder = await this.fulfillmentOrderStore.undoPackaged(
					{ fulfillmentOrderId: this.selectedFulfillmentOrder.id },
					sourceOfChange.undoScanLabelLabelingMode
				);
				this.selectedFulfillmentOrder.fulfillmentOrderStatus = fulfillmentOrder.fulfillmentOrderStatus;
				this.boxedFulfillmentOrders.pop();
				this.packagedFulfillmentOrders.pop();
				this.removeElementFromActionsList();
				this.lastAction = null;
			}
			// Undo from Palletized status to Label Created.
			else if (this.lastAction === 'palletize') {
				await this.shipmentInfoStore.undoPalletize(
					{
						trackingNumber: this.steps.finalStep.form.$('trackingNumber').value,
						isFromLabelingMode: true,
					},
					sourceOfChange.undoPalletizeLabelingMode
				);

				//this.selectedFulfillmentOrder.fulfillmentOrderStatus = fulfillmentOrder.fulfillmentOrderStatus;
				this.rootStore.notificationStore.success(
					'Undo from Palletize to Label Created successful. UI Status change not yet implemented.'
				);
				this.boxedFulfillmentOrders.pop();
				this.packagedFulfillmentOrders.pop();
				this.removeElementFromActionsList();
				this.lastAction = null;
			}
			// Nothing to Undo
			else {
				this.rootStore.notificationStore.error('FULFILLMENTORDER.LABELINGMODE.MESSAGE.UNDONOTHING');
			}
		} catch (err) {
			this.rootStore.notificationStore.error('FULFILLMENTORDER.LABELINGMODE.MESSAGE.UNDOERROR');
		}
	}

	@action.bound
	onAbort = async () => {
		this.suspend();
		try {
			await this.fulfillmentOrderProcessingStore.abortLabelingMode(
				{
					initialFulfillmentOrder: this.initialFulfillmentOrder,
					splittedFulfillmentOrderIds: this.splittedFulfillmentOrderIds,
				},
				sourceOfChange.abortLabelingMode
			);
			this.rootStore.notificationStore.success([
				'FULFILLMENTORDER.LABELINGMODE.MESSAGE.ABORTSUCCESS',
				{ id: this.initialFulfillmentOrder.fulfillmentOrderId },
			]);
		} catch (err) {
			if (
				err.statusCode == 400 &&
				_.get(err, 'data.message') == 'FULFILLMENTORDER.LABELINGMODE.FULFILLMENTORDERCHANGED'
			) {
				this.rootStore.notificationStore.error(['FULFILLMENTORDER.LABELINGMODE.FULFILLMENTORDERCHANGED']);
			} else {
				this.rootStore.notificationStore.error([
					'FULFILLMENTORDER.LABELINGMODE.MESSAGE.ABORTFAILURE',
					{ id: this.initialFulfillmentOrder.fulfillmentOrderId },
				]);
			}
		}
		this.resume();
	};

	@action.bound
	removeElementFromActionsList = () => {
		const actionIndex = _.findIndex(this.actionsDone, item => item === this.lastAction);
		this.actionsDone.splice(actionIndex, 1);
	};

	getAbortConfirmModalProps = () => {
		return {
			onConfirm: async () => {
				this.isUndoClicked = true;
				await this.onAbort();
				this.modalParams.success();
			},
			onClose: async () => {
				this.isUndoClicked = false;
				if (this.timeoutAction != null) {
					await this.actionAfterTimeout();
				}
			},
			message: ['FULFILLMENTORDER.LABELINGMODE.MESSAGE.ABORTCONFIRMATION'],
		};
	};

	@action.bound
	actionAfterTimeout = async () => {
		setTimeout(async () => {
			// If user didn't click undo or Abort, continue with resetSteps.
			if (!this.isUndoClicked) {
				if (this.timeoutAction === 'stepTwo') {
					this.selectedFulfillmentOrder = this.newFulfillmentOrder;
					_.forEach(this.selectedFulfillmentOrder.fulfillmentOrderItems, item => {
						item.scannedQuantity = 0;
						item.isScanned = false;
					});
					this.closeBoxDisabled = this.isCloseBoxDisabled();
					this.steps.dimensionsStep.isCompleted = false;
					this.changeStep(2);
				} else if (this.timeoutAction === 'stepOne') {
					this.selectedFulfillmentOrder = null;
					this.resetSteps();
					this.changeStep(1);
				} else if (this.timeoutAction === 'stepZero') {
					if (_.get(this.palletDropdownStore, 'value.value')) {
						var selectedPalletId = this.palletDropdownStore.value.value;
						this.palletDropdownStore.clear();
						await this.palletDropdownStore.reload();
						var palletToSelect = _.find(this.palletDropdownStore.options, palletOption => {
							return palletOption.value == selectedPalletId;
						});
						await this.palletDropdownStore.setValue(palletToSelect);
					}
					this.resetSteps();
					this.isUpcScanned = false;
					this.isFulfillmentOrderIdEntered = false;
					this.steps.labelingStep.form.clear();
				}
				this.timeoutAction = null;
			}
		}, 4000);
	};

	@action.bound
	openAbortConfirmModal() {
		this.isUndoClicked = true;
		this.openConfirmationModal(this.getAbortConfirmModalProps());
	}

	@action.bound
	setInputs = (input, field) => {
		const key = field.key + 'Input';
		this[key] = input;
	};

	@action.bound
	resetSteps = () => {
		_.forEach(this.steps, step => {
			step.isCompleted = false;
		});
	};

	// Restores the "just opened" LTL packing state after a BOL has been printed: hides the step workflow and
	// re-shows the fulfillment-order-id entry input (rendered while isLtlPackingMode && !isFulfillmentOrderIdEntered),
	// clearing the order that was just packaged so the worker can start the next one.
	@action.bound
	returnToLtlPackingStart = () => {
		this.resetSteps();
		this.resetMessages();
		this.isUpcScanned = false;
		this.isFulfillmentOrderIdEntered = false;
		this.selectiongFoStepDisabled = false;
		this.selectedFulfillmentOrder = null;
		this.initialFulfillmentOrder = null;
		this.fulfillmentOrders = [];
		this.steps.labelingStep.form.clear(); // Clears the fulfillmentOrderId entry field.
		// Delete by field: keys stay sparse after a del(), so pallets[i] no longer addresses them.
		const pallets = this.steps.dimensionsStep.form.$('pallets');
		pallets.map(pallet => pallet).forEach(pallet => pallet.del());
		this.steps.dimensionsStep.form.$('palletUpc').clear();
		this.changeStep(1);
	};

	@action.bound
	handleShowAll = async (fulfillmentOrderId, packagedOrders = false) => {
		_.forEach(packagedOrders ? this.packagedFulfillmentOrders : this.fulfillmentOrders, fulfillmentOrder => {
			if (fulfillmentOrder.id === fulfillmentOrderId) {
				fulfillmentOrder.showAll = true;
			}
		});
		await this.changeStep(1); // This will force re-render, but this function should not be called. Unable to find better solution at the moment.
	};

	@action.bound
	resetMessages = () => {
		this.scanUpcMessage = null;
		this.scanLabelMessage = null;
		this.closeBoxMessage = null;
		this.createLabelMessage = null;
		this.dimensionsStepErrorMessage = null;
		this.palletScanMessage = null;
	};

	@action.bound
	setRef = ref => {
		if (ref != null) {
			if (ref.constructor.name === 'Async') {
				this.selectReference = ref;
			} else {
				this.barcodeRef = ref;
			}
		}
	};

	isCloseBoxDisabled = () => {
		if (
			_.every(this.selectedFulfillmentOrder.fulfillmentOrderItems, item => {
				return item.scannedQuantity === 0;
			})
		) {
			// Close box button is enabled only if at least one of the items is scanned.
			return true;
		} else {
			return false;
		}
	};

	@action.bound
	onDeletePallet = async item => {
		this.openConfirmationModal(this.getPalletDeleteConfirmModalProps(item));
	};

	getPalletDeleteConfirmModalProps = item => {
		return {
			onConfirm: async () => await this.deletePallet(),
			message: ['LOOKUPDELETECONFIRM.PALLET', { name: item.label }],
			data: item,
		};
	};

	// The dispatch-time Priority1 re-quote returned the originally selected carrier/service at a different price:
	// the worker either books the new rate or puts the fulfillment order on hold.
	@action.bound
	openLtlRateChangedModal = (originalRate, newRate) => {
		this.openConfirmationModal({
			onConfirm: async () => await this.printLabel({ acceptedLtlRate: newRate }),
			onCancel: async () => await this.printLabel({ holdLtl: true }),
			yesLabel: 'FULFILLMENTORDER.LABELINGMODE.RATECHANGEDMODAL.ACCEPT',
			noLabel: 'FULFILLMENTORDER.LABELINGMODE.RATECHANGEDMODAL.HOLD',
			message: [
				'FULFILLMENTORDER.LABELINGMODE.RATECHANGEDMODAL.MESSAGE',
				{
					carrierService:
						(this.selectedFulfillmentOrder &&
							this.selectedFulfillmentOrder.carrierService &&
							this.selectedFulfillmentOrder.carrierService.name) ||
						'',
					originalRate: originalRate != null ? `$${Number(originalRate).toFixed(2)}` : '-',
					newRate: newRate != null ? `$${Number(newRate).toFixed(2)}` : '-',
				},
			],
		});
	};

	@action.bound
	onWarehouseChange = async value => {
		if (isSome(value)) {
			await this.initializePalletDropdown();
		}
	};
}

export default labelingModeViewStore;
